import {SignalFunc, Fn, forceSignal} from "./signal.js";
import {IStackDisposable} from "./disposable.js";

const SLOTS = Symbol('slots');

export function slot<This extends IStackDisposable, Prop extends keyof This, P extends unknown[], R, Target = This[Prop]>(
    observedProperty: Prop, targetSignal: string | ((obj: Target) => SignalFunc<P, R>))
    : (fn_: Fn<P, R>, ctx: ClassMethodDecoratorContext<This>) => void

export function slot<This extends IStackDisposable, Observed, Prop extends keyof Observed, P extends unknown[], R, Target = Observed[Prop]>(
    observedObject: (this: This, $: This) => Observed, observedProperty: Prop, targetSignal: string | ((obj: Target) => SignalFunc<P, R>))
    : (fn_: Fn<P, R>, ctx: ClassMethodDecoratorContext<This>) => void

export function slot<This extends IStackDisposable, Target, P extends unknown[], R>(
    targetObject: (this: This, $: This) => Target, targetSignal: string | ((obj: Target) => SignalFunc<P, R>))
    : (fn_: Fn<P, R>, ctx: ClassMethodDecoratorContext<This>) => void
;
export function slot<This extends IStackDisposable, Prop extends keyof This, Observed, Target, P extends unknown[], R>(
    a: string | ((this: This, $: This) => Target) | ((this: This, $: This) => Observed), b: string | ((obj: Target) => SignalFunc<P, R>) | keyof Observed, c?: string | ((obj: Target) => SignalFunc<P, R>))
    : (fn_: Fn<P, R>, ctx: ClassMethodDecoratorContext<This>) => void {

    let targetObjectRef: ((this: This, $: This) => Target) | null = null, observedProperty: string | null = null, observedObjectRef: ((this: This, $: This) => Observed) | null = null, targetSignal: string | ((obj: Target) => SignalFunc<P, R>);
    if (typeof a === 'string' && (typeof b === 'string' || typeof b === 'function') && typeof c === 'undefined') {
        observedProperty = a; targetSignal = b;
    } else if (typeof a === 'function' && typeof b === 'string' && (typeof c === 'string' || typeof c === 'function')) {
        observedObjectRef = a as (this: This, $: This) => Observed; observedProperty = b; targetSignal = c;
    } else if (typeof a === 'function' && (typeof b === 'string' || typeof b === 'function') && typeof c === 'undefined') {
        targetObjectRef = a as (this: This, $: This) => Target; targetSignal = b;
    } else {
        throw "invalid overload for slot"
    }
    const deref = function(t : Target) {
        return (typeof targetSignal === 'function' ? targetSignal(t as Target) : t[targetSignal] as SignalFunc<P, R>);
    }
    return function(fn_: Fn<P, R>, ctx: ClassMethodDecoratorContext<This>): void {
        ctx.addInitializer(function initializer(this: This) {
            var targetObject = targetObjectRef ? targetObjectRef.call(this, this) as Target : null;
            var observedObject = observedObjectRef ? observedObjectRef.call(this, this) as Observed : this;
            var fn = fn_.bind(this) as Fn<P, R>;
            var deregister : Disposable | null = null;
            this[Symbol.dispose].defer(function() {
                if (deregister) {
                    deregister[Symbol.dispose]();
                }
            });
            const register = function(oldTarget: Target | null, newTarget: Target | null) {
                if (deregister) {
                    deregister[Symbol.dispose]();
                    deregister = null;
                }
                if (newTarget) {
                    try {
                        deregister = deref(newTarget).connect(fn);
                    } catch (e) {
                        console.warn("Invalid target on @slot(" + a + ", " + b + ", " + c + ")", newTarget)
                        throw new Error("Unable to connect to " + targetSignal + " on " + newTarget, {cause: e});
                    }
                }
            }

            if (targetObjectRef) {
                if (targetObject && deref(targetObject)) {
                    register(null, targetObject);
                } else if (SLOTS in this) {
                    (this[SLOTS] as any[]).push(() => register(null, targetObjectRef.call(this, this)));
                } else {
                    throw "targetObject not found"
                }
            } else if (observedObject && observedProperty) {
                const observable = forceSignal(observedObject, observedProperty + '$');
                observable.connect(register);

                try {
                    if (observedObject[observedProperty]) register(null, observedObject[observedProperty]);
                } catch {}
            } else {
                console.warn("invalid targetObject type", targetObject, observedObject, observedProperty);
                throw "invalid targetObject type";
            }
        });
    };
}

